import React, { FC } from 'react';
import { Form, Formik } from 'formik';
import { number, object, string } from 'yup';

// Models
import {
  RefundFormValue,
  RefundPeriod,
} from '../../../models/Forms/RefundFormValue';
import { FormikDropdownOption } from '../../../models/Formik';

// Components
import { Button } from '../../Button';
import { FormikDropdown, FormikInput, FormikTextArea } from '../../formik';

// i18n
import { useTranslation } from '../../../translations/useTranslation';

interface Props {
  formValues: RefundFormValue;
  isRedirecting: boolean;
  onSubmit: (values: RefundFormValue) => Promise<void>;
  introductionText?: string;
}

const RefundForm: FC<Props> = ({
  formValues,
  isRedirecting,
  onSubmit,
  introductionText,
}) => {
  const { t, keys } = useTranslation();

  const monthOptions: FormikDropdownOption<RefundPeriod>[] = [
    { label: `2 ${t(keys.uiMonths).toLocaleLowerCase()}`, value: 2 },
    { label: `3 ${t(keys.uiMonths).toLocaleLowerCase()}`, value: 3 },
    { label: `6 ${t(keys.uiMonths).toLocaleLowerCase()}`, value: 6 },
    { label: `12 ${t(keys.uiMonths).toLocaleLowerCase()}`, value: 12 },
    { label: `18 ${t(keys.uiMonths).toLocaleLowerCase()}`, value: 18 },
    { label: `24 ${t(keys.uiMonths).toLocaleLowerCase()}`, value: 24 },
    { label: t(keys.refundFormOtherSuggestions), value: 0 },
  ];

  const validationSchema = object().shape({
    amount: number()
      .min(1, t(keys.refundFormAmountValidationMessage))
      .required(t(keys.errorRequired)),
    months: number()
      .oneOf(
        monthOptions.map((o) => o.value),
        t(keys.refundFormMonthValidationMessage)
      )
      .required(t(keys.errorRequired)),
    name: string().required(t(keys.errorRequired)),
    email: string()
      .required(t(keys.errorRequired))
      .email(t(keys.errorInvalidEmail)),
    reference: string().required(t(keys.errorRequired)),
    suggestions: string().when('months', {
      is: 0,
      then: string().required(t(keys.errorRequired)),
      otherwise: string(),
    }),
  });

  const getHighestValidPaymentPeriod = (amount: number): RefundPeriod => {
    if (amount >= 30000) {
      return 24;
    }
    if (amount >= 20000) {
      return 18;
    }
    if (amount >= 10000) {
      return 12;
    }
    return 6;
  };

  const getValidPaymentPeriodOptions = (
    amount: number
  ): FormikDropdownOption<RefundPeriod>[] => {
    const highestValidPaymentPeriod = getHighestValidPaymentPeriod(amount);
    return monthOptions.filter((o) => o.value <= highestValidPaymentPeriod);
  };

  const isValidAmountForPeriod = (
    amount: number,
    period: RefundPeriod | undefined
  ) => {
    const validPeriods = getValidPaymentPeriodOptions(amount);
    return validPeriods.some((p) => p.value === period);
  };

  return (
    <div>
      <h2 className="form-app__heading">{t(keys.refundFormTitle)}</h2>
      {introductionText && (
        <div
          dangerouslySetInnerHTML={{
            __html: introductionText,
          }}
        ></div>
      )}
      <Formik
        initialValues={formValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, setFieldValue, values, submitForm }) => (
          <Form>
            <fieldset className="aea-form-fieldset">
              <legend>{t(keys.refundFormAmountLegend)}</legend>
              <FormikInput
                label={`${t(keys.formTotalAmount)} *`}
                type="number"
                name="amount"
                placeholder={t(keys.refundFormAmountPlaceholder)}
                step={100}
                onChange={(value) => {
                  const amount = parseFloat(value);
                  if (amount <= 0) {
                    setFieldValue('amount', 0);
                  }
                  const isValid = isValidAmountForPeriod(amount, values.months);
                  if (!isValid) {
                    setFieldValue('months', undefined);
                  }
                }}
              />
            </fieldset>

            <fieldset className="aea-form-fieldset">
              <legend>{t(keys.refundFormPaymentPlanLegend)}</legend>
              <FormikDropdown
                label={`${t(keys.formNumberOfMonths)} *`}
                subLabel={t(keys.refundFormPaymentPlanSublabel)}
                options={getValidPaymentPeriodOptions(values.amount)}
                placeholder={t(keys.refundFormPaymentPlanPlaceholder)}
                onChange={(option) => {
                  if (option.value !== 0) {
                    // Clear suggestions if a valid period is selected
                    setFieldValue('suggestions', undefined);
                  }
                }}
                name="months"
              />
              {values.months === 0 && (
                <FormikTextArea
                  label={`${t(keys.refundFormSuggestionsLabel)} *`}
                  subLabel={t(keys.refundFormSuggestionsSublabel)}
                  rows={3}
                  name="suggestions"
                />
              )}
            </fieldset>

            <fieldset className="aea-form-fieldset">
              <legend>{t(keys.uiPersonalInformation)}</legend>
              <FormikInput
                label={`${t(keys.formName)} *`}
                type="text"
                name="name"
              />
              <FormikInput
                label={`${t(keys.formEmailAddress)} *`}
                type="email"
                name="email"
              />
              <FormikInput
                label={`${t(keys.formPhoneNumber)}`}
                type="text"
                name="phone"
              />
              <FormikInput
                label={`${t(keys.formReference)} *`}
                subLabel={t(keys.refundFormReferenceSublabel)}
                type="text"
                name="reference"
              />

              <input
                type="text"
                name="honeyPot"
                tabIndex={-1}
                autoComplete="off"
                onChange={(event) => {
                  setFieldValue('honeyPot', event.target.value);
                }}
                style={{ position: 'absolute', left: '-9999px' }}
              />
            </fieldset>

            <Button
              title={t(keys.uiSubmit)}
              onClick={submitForm}
              disabled={!isValid}
              isLoading={isSubmitting || isRedirecting}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RefundForm;
